import React, { useCallback, useState } from 'react';
import classnames from 'classnames';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';
import { FormattedMessage } from 'react-intl';

import { Text, TextType } from 'ui-components/components/text';
import { Button } from 'ui-components/components/button';

import Global from '../../../../../../store/global-selectors';
import { importProducts } from '../../../../../../store/products/grid/product.grid.actions';
import FileUploader from '../../../../../products-page/products/modals/import/import-file-uploader';
import {
  PRODUCT_MODAL_TYPE,
  CHOOSE_IMPORT_DIALOGUE,
  IMPORT_IN_PROGRESS_DIALOGUE,
  IDENTIFY_BY_SKU,
} from '../../../../../products-page/products/constants/import-products';
import ExampleOfProductFile from '../../images/example-of-properly-entered-ship-from-locations.svg';

import messages from '../../messages';
import commonStyles from '../../../style.module.less';
import style from '../../style.module.less';

const UploadActiveShippingLocationsStep = ({ currentSelectedFile = null, setCurrentSelectedFile }) => {
  const dispatch = useDispatch();
  const importProductsStarted = useSelector(Global.selectProductproductImportStarted);
  const importProductsComplete = useSelector(Global.selectProductproductImportCompleted);

  const [file, setFile] = useState(currentSelectedFile);
  const [modalDialogue, setModalDialogue] = useState(currentSelectedFile === null ? CHOOSE_IMPORT_DIALOGUE : IMPORT_IN_PROGRESS_DIALOGUE);

  const identifyBy = IDENTIFY_BY_SKU;
  const overwriteExisting = true;
  const modalType = PRODUCT_MODAL_TYPE;

  const onConfirm = useCallback(() => {
    dispatch(
      importProducts({
        file,
        identifyBy,
        overwriteExisting,
        importType: modalType,
      }),
    );
    setModalDialogue(IMPORT_IN_PROGRESS_DIALOGUE);
    setCurrentSelectedFile(file);
  }, [dispatch, file, identifyBy, overwriteExisting, modalType, setCurrentSelectedFile]);

  return (
    <div className={commonStyles['step-content']}>
      <Text className={style['step-title']} textType={TextType.H3} value={messages.addShipFromLocations} />
      <Text className={style['step-paragraph']} textType={TextType.Paragraph} value={messages.directionsForEditingProductFile1} />
      <ol className={classnames(commonStyles['auto-routing-bulleted-list'], style['auto-routing-wizard-list'])}>
        <li className={commonStyles['.auto-routing-numbered-list-item']}>
          <Text
            formattedMessageValues={{
              b: (chunks) => <span className={commonStyles.bolded}>{chunks}</span>,
            }}
            textType={TextType.Paragraph}
            value={messages.directionsForEditingProductFileStep1}
          />
        </li>
        <li className={commonStyles['.auto-routing-numbered-list-item']}>
          <Text textType={TextType.Paragraph} value={messages.directionsForEditingProductFileStep2} />
        </li>
        <li className={commonStyles['.auto-routing-numbered-list-item']}>
          <Text
            formattedMessageValues={{
              b: (chunks) => <span className={commonStyles.bolded}>{chunks}</span>,
            }}
            textType={TextType.Paragraph}
            value={messages.directionsForEditingProductFileStep3}
          />
        </li>
        <li className={commonStyles['.auto-routing-numbered-list-item']}>
          <Text
            formattedMessageValues={{
              b: (chunks) => <span className={commonStyles.bolded}>{chunks}</span>,
            }}
            textType={TextType.Paragraph}
            value={messages.directionsForEditingProductFileStep4}
          />
        </li>
      </ol>
      {modalDialogue === IMPORT_IN_PROGRESS_DIALOGUE && file && importProductsComplete && (
        <Text textType={TextType.H4} value={messages.fileUploadedLabel} />
      )}
      {modalDialogue === IMPORT_IN_PROGRESS_DIALOGUE && file && importProductsStarted && (
        <Text textType={TextType.Caption} value={messages.productImportStartedDuringSetup} />
      )}
      {modalDialogue === CHOOSE_IMPORT_DIALOGUE && file && (
        <Button disabled={!file} onClick={onConfirm} theme='primary'>
          <FormattedMessage {...messages.uploadFileButtonLabel} />
        </Button>
      )}
      {(modalDialogue === CHOOSE_IMPORT_DIALOGUE || modalDialogue === IMPORT_IN_PROGRESS_DIALOGUE) && (
        <FileUploader
          className={commonStyles['import-pill']}
          file={file}
          name='productImport'
          setFile={setFile}
          setModalDialogue={setModalDialogue}
          shouldOnlyDisplayFilename={file !== null}
          theme='primary'
        />
      )}
      <Text className={style['step-title']} textType={TextType.H4} value={messages.addShipFromLocations} />
      <img alt='Example Product File' className={style['product-table-img']} src={ExampleOfProductFile} />
    </div>
  );
};

UploadActiveShippingLocationsStep.propTypes = {
  currentSelectedFile: PropTypes.shape({
    name: PropTypes.string.isRequired,
  }),
  setCurrentSelectedFile: PropTypes.func.isRequired,
};

export default UploadActiveShippingLocationsStep;
